import React from 'react';
import { Plus, Minus } from 'lucide-react';
import type { RoomPreference } from '../../../types/business';

interface RoomSelectorProps {
  roomPreferences: RoomPreference[];
  onChange: (rooms: RoomPreference[]) => void;
  maxTravelers?: number;
}

export default function RoomSelector({ roomPreferences, onChange, maxTravelers = 20 }: RoomSelectorProps) {
  const totalCapacity = roomPreferences.reduce((sum, room) => {
    const capacity = room.type === 'single' ? 1 : 
                    room.type === 'double' ? 2 :
                    room.type === 'triple' ? 3 : 2;
    return sum + (capacity * room.count);
  }, 0);

  const handleRoomCountChange = (type: RoomPreference['type'], count: number) => {
    const existingRoom = roomPreferences.find(room => room.type === type);
    
    if (existingRoom) {
      onChange(roomPreferences.map(room => 
        room.type === type ? { ...room, count } : room
      ).filter(room => room.count > 0));
    } else if (count > 0) {
      onChange([...roomPreferences, { type, count }]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <p className="text-blue-800 font-medium">
          Capacité totale: {totalCapacity} personne{totalCapacity > 1 ? 's' : ''}
        </p>
        {maxTravelers && totalCapacity > maxTravelers && (
          <p className="text-red-600 text-sm mt-1">
            La capacité dépasse le nombre maximum de participants ({maxTravelers})
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(['single', 'double', 'triple', 'suite'] as const).map(type => {
          const room = roomPreferences.find(r => r.type === type) || { type, count: 0 };
          const capacity = type === 'single' ? 1 : 
                         type === 'double' ? 2 :
                         type === 'triple' ? 3 : 2;

          return (
            <div key={type} className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <div>
                  <h4 className="font-medium">
                    Chambre {type === 'single' ? 'Simple' :
                            type === 'double' ? 'Double' :
                            type === 'triple' ? 'Triple' : 'Suite'}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {capacity} personne{capacity > 1 ? 's' : ''} par chambre
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleRoomCountChange(type, Math.max(0, room.count - 1))}
                    className="p-1 rounded-full hover:bg-gray-100"
                    disabled={room.count === 0}
                  >
                    <Minus className="w-5 h-5 text-gray-600" />
                  </button>
                  <span className="w-8 text-center font-medium">{room.count}</span>
                  <button
                    type="button"
                    onClick={() => handleRoomCountChange(type, room.count + 1)}
                    className="p-1 rounded-full hover:bg-gray-100"
                    disabled={maxTravelers && (totalCapacity + capacity) > maxTravelers}
                  >
                    <Plus className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
              {room.count > 0 && (
                <p className="text-sm text-gray-600">
                  Capacité totale: {room.count * capacity} personne{room.count * capacity > 1 ? 's' : ''}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}